import { useState } from 'react';
import { ChevronDown, ChevronRight, Settings2 } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Props for {@link AdvancedSettingsSection}.
 */
interface AdvancedSettingsSectionProps {
  /** Section title (e.g. "Advanced settings"). */
  title: string;
  /** Short description shown under the title, e.g. "most customers can leave these as-is". */
  description?: string;
  /** The advanced fields rendered when the section is expanded. */
  children: React.ReactNode;
  /** Whether the section starts expanded. Defaults to collapsed (opt-in). */
  defaultOpen?: boolean;
}

/**
 * A collapsible, opt-in "Advanced settings" section. Collapsed by default so the happy path is
 * unchanged; expanding it reveals replication-impacting settings pre-filled with sensible defaults.
 * Reused by the profile-creation wizard and the post-creation settings screens so the two surfaces
 * share the same advanced inputs and can't drift.
 */
export function AdvancedSettingsSection({
  title,
  description,
  children,
  defaultOpen = false,
}: AdvancedSettingsSectionProps) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="rounded-md border">
      <button
        type="button"
        onClick={() => { setOpen((prev) => !prev); }}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-2 p-3 text-left"
      >
        <div className="flex items-start gap-2">
          <Settings2 className="mt-0.5 h-4 w-4 text-muted-foreground shrink-0" />
          <div>
            <div className="text-sm font-medium">{title}</div>
            {description && <p className="text-xs text-muted-foreground">{description}</p>}
          </div>
        </div>
        {open ? (
          <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
        ) : (
          <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
        )}
      </button>
      <div className={cn('border-t p-3 space-y-4', !open && 'hidden')}>{children}</div>
    </div>
  );
}
